import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, CalendarDays, Eye, FolderOpen, Plus, Sparkles } from "lucide-react";
import TabPage, { TabItemProps } from "../../components/dynamic/Tab";
import { ApiService } from "../../services/api";
import type { FileItem, MaarifTestFileMetadata } from "../../types/file";
import checkPermission from "../../utils/permission";

const formatDate = (value: string) =>
  new Date(value).toLocaleDateString("tr-TR", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const isMaarifTest = (item: FileItem): item is FileItem & { metadata: MaarifTestFileMetadata } =>
  Boolean(item.metadata && (item.metadata as MaarifTestFileMetadata).kind === "maarif_test");

export default function MaarifTestListPage() {
  const navigate = useNavigate();
  const canList = checkPermission("maarif_test_list");
  const canCreate = checkPermission("maarif_test_add");
  const canOpenInQuestionBank = checkPermission("question_bank_list");
  const [refreshKey] = useState(0);

  const actions = useMemo(
    () =>
      canCreate ? (
        <button
          onClick={() => navigate("/maarif-test-generator/new")}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium"
        >
          <Plus className="w-4 h-4" />
          Yeni Test Oluştur
        </button>
      ) : null,
    [canCreate, navigate]
  );

  const renderItem = (item: FileItem) => {
    const metadata = (item.metadata || {}) as MaarifTestFileMetadata;

    return (
      <div key={item.id} className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm hover:shadow-md transition">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-center gap-2 mb-2">
              <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-700 flex items-center justify-center">
                <Sparkles className="w-5 h-5" />
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate">{item.originalName}</h3>
                <div className="text-xs text-gray-500">Maarif test kaydı</div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm text-gray-600">
              <div className="inline-flex items-center gap-2">
                <BookOpen className="w-4 h-4 text-gray-400" />
                {metadata.lessonTitle || "Ders bilgisi yok"}
              </div>
              <div className="inline-flex items-center gap-2">
                <FolderOpen className="w-4 h-4 text-gray-400" />
                {metadata.gradeLabel || "Sınıf bilgisi yok"}
              </div>
              <div className="inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-gray-400" />
                {metadata.questionCount || 0} soru • {metadata.imageQuestionCount || 0} görselli
              </div>
              <div className="inline-flex items-center gap-2">
                <CalendarDays className="w-4 h-4 text-gray-400" />
                Oluşturuldu: {formatDate(item.createdAt)}
              </div>
            </div>

            <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 text-sm text-slate-600">
              {metadata.topic || "Konu bilgisi bulunmuyor."}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2 shrink-0">
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200">
              Maarif AI
            </span>
            <button
              onClick={() => navigate(`/question-bank-indesign/${metadata.questionBankId}/edit`)}
              disabled={!canOpenInQuestionBank}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 text-sm"
            >
              <Eye className="w-4 h-4" />
              Dizgicide Aç
            </button>
          </div>
        </div>
      </div>
    );
  };

  const tabs = useMemo<TabItemProps[]>(
    () => [
      {
        type: "list",
        title: "Tüm Testler",
        path: "maarif-test-files",
        tabId: "all-maarif-tests",
        isSearch: true,
        isPagination: true,
        refreshKey,
        actions,
        emptyMessage: "Henüz kaydedilmiş bir Maarif test kaydı bulunmuyor.",
        fetcher: async () => {
          const response = await ApiService.listMaarifTests();
          const rows = (response.rows || []).filter((item) => isMaarifTest(item));
          return {
            data: rows,
          };
        },
        dataProcessor: (rows, searchTerm) => {
          const term = String(searchTerm || "").trim().toLocaleLowerCase("tr-TR");
          const filtered = term
            ? rows.filter((item) => {
                const file = item as FileItem;
                const metadata = (file.metadata || {}) as MaarifTestFileMetadata;
                return [file.originalName, metadata.lessonTitle, metadata.gradeLabel, metadata.topic, metadata.difficulty]
                  .filter(Boolean)
                  .some((value) => String(value).toLocaleLowerCase("tr-TR").includes(term));
              })
            : rows;

          return [...filtered].sort((a, b) => {
            const aTime = new Date((a as FileItem).createdAt).getTime();
            const bTime = new Date((b as FileItem).createdAt).getTime();
            return bTime - aTime;
          });
        },
        clientPageSize: 12,
        item: (data) => renderItem(data as FileItem),
      },
    ],
    [actions, refreshKey]
  );

  if (!canList) {
    return (
      <div className="min-h-[calc(100vh-120px)] flex items-center justify-center px-6">
        <div className="max-w-md rounded-2xl border border-gray-200 bg-white p-8 text-center shadow-sm">
          <div className="text-lg font-semibold text-gray-900">Test Oluşturma erişimi yok</div>
          <div className="mt-2 text-sm text-gray-600">Bu sayfayı görüntüleme yetkiniz bulunmuyor.</div>
        </div>
      </div>
    );
  }

  return <TabPage title="Test Oluşturma" tabs={tabs} />;
}
